import { useEffect, useMemo, useRef, useState } from 'react';
import { goBack, navigate } from '../app/router';
import { NoteCard } from '../components/note/NoteCard';
import { useAssetUrl } from '../components/note/useAssetUrl';
import { Icon } from '../components/ui/Icon';
import { Group, Row, ScreenHeader, Sheet, Switch } from '../components/ui/ui';
import { resolveLook } from '../designs/look';
import { importImage } from '../images/imageTools';
import { formatDateTime } from '../lib/format';
import { displayTitle, isEmptyNote } from '../notes/model';
import { resolveActiveNote } from '../scheduling/activeNote';
import { store, useStore } from '../state/store';
import { putAsset } from '../storage/repo';
import type { Line, Settings } from '../storage/schemas';
import { canvasToBlob, renderLockScreenImage, renderNoteCanvas, shareOrDownload } from './exportImage';
import { DEVICES, getDevice, getWallpaper, wallpaperCss, WALLPAPERS } from './wallpapers';
import './preview.css';

function useClock() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function toLocalInput(ts: number) {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function InstallHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Install MemoNest" tall>
      <div className="help-pane">
        <h3>iPhone & iPad (Safari)</h3>
        <ol>
          <li>Open MemoNest in Safari.</li>
          <li>
            Tap <b>Share</b> <Icon name="share" size={16} /> → <b>Add to Home Screen</b> → <b>Add</b>.
          </li>
          <li>Open MemoNest from the new Home Screen icon. Notifications only work when opened this way (iOS 16.4+).</li>
        </ol>
        <h3>Android (Chrome)</h3>
        <ol>
          <li>
            Tap <b>⋮</b> → <b>Install app</b> (or <b>Add to Home screen</b>).
          </li>
          <li>Open it from the app drawer. Allow notifications when MemoNest asks.</li>
        </ol>
        <h3>Desktop (Chrome, Edge)</h3>
        <ol>
          <li>Click the install icon in the address bar, or ⋮ → Install MemoNest.</li>
        </ol>
        <h3>Putting a note on your real lock screen</h3>
        <p>
          A web app can’t add widgets to the lock screen. The closest you can get today: tap <b>Save lock-screen wallpaper</b> in the preview, then set the saved image as your lock-screen wallpaper
          (iPhone: Photos → Share → Use as Wallpaper; Android: Gallery → Set as wallpaper). Re-export when the note changes.
        </p>
        <p>Real, live lock-screen widgets need a native companion app — see the project README.</p>
      </div>
    </Sheet>
  );
}

export function PreviewScreen() {
  const notes = useStore((s) => s.notes);
  const designs = useStore((s) => s.designs);
  const schedules = useStore((s) => s.schedules);
  const manualActive = useStore((s) => s.meta.activeNoteId);
  const active = useStore((s) => s.active);
  const lp = useStore((s) => s.settings.lockPreview);
  const showEmpty = useStore((s) => s.settings.showEmptyNotes);
  const now = useClock();
  const [at, setAt] = useState<number | null>(null);
  const [full, setFull] = useState(false);
  const [help, setHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const customUrl = useAssetUrl(lp.customWallpaperAssetId);

  const resolution = useMemo(() => {
    if (at === null) return active;
    const exists = (id: string) => !!notes[id] && !notes[id].deletedAt;
    return resolveActiveNote(at, schedules, manualActive, exists);
  }, [at, active, notes, schedules, manualActive]);
  const note = resolution.noteId ? notes[resolution.noteId] : undefined;
  const look = note ? resolveLook(note, designs) : null;
  const device = getDevice(lp.device);
  const wp = getWallpaper(lp.wallpaper);
  const setLp = (patch: Partial<Settings['lockPreview']>) => store.updateSettings({ lockPreview: { ...lp, ...patch } });
  const clockTime = at ?? now;
  const darkText = !customUrl && !wp.dark;

  const toggle = (l: Line) => note && store.toggleLine(note.id, l.id);

  const exportImage = async (kind: 'note' | 'wallpaper') => {
    if (!note || !look) return;
    setBusy(true);
    try {
      let custom: HTMLImageElement | null = null;
      if (customUrl) {
        custom = new Image();
        custom.src = customUrl;
        await custom.decode();
      }
      const canvas =
        kind === 'note'
          ? await renderNoteCanvas(note, look)
          : await renderLockScreenImage(note, look, { wallpaper: lp.wallpaper, customWallpaper: custom, device: lp.device, position: lp.position, scale: lp.scale, contrast: lp.contrast, showClock: lp.showClock });
      const how = await shareOrDownload(await canvasToBlob(canvas), kind === 'note' ? 'memonest-note.png' : `memonest-lockscreen-${device.id}.png`);
      if (how === 'downloaded') store.toast('Image saved to your downloads.', 'success');
    } catch (err) {
      store.toast(`Export failed: ${(err as Error).message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const phone = (
    <div
      className={`lp-phone ${full ? 'is-full' : ''}`}
      style={{ aspectRatio: `${device.w} / ${device.h}`, ['--phone-radius' as string]: `${(device.radius / device.w) * 100}cqw` }}
      aria-label="Simulated lock screen preview"
      role="group"
    >
      <div className="lp-screen" style={{ background: customUrl ? `center / cover no-repeat url("${customUrl}")` : wallpaperCss(wp) }}>
        {lp.contrast && <div className="lp-dim" aria-hidden="true" />}
        <div className={`lp-status ${darkText ? 'is-dark' : ''}`} aria-hidden="true">
          <span>{new Date(clockTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
          <span className="lp-status-icons">▮▮▮ ◔</span>
        </div>
        {lp.showClock && (
          <div className={`lp-clock ${darkText ? 'is-dark' : ''}`}>
            <span className="lp-date">{new Date(clockTime).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}</span>
            <span className="lp-time">{new Date(clockTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M$/i, '')}</span>
          </div>
        )}
        {note && look && (showEmpty || !isEmptyNote(note)) ? (
          <div
            className="lp-note"
            style={{
              ['--lp-note-h' as string]: `${(92 * lp.scale * 0.936) / (note.style.compact ? 4.4 : 2.2)}cqw`,
              top: `calc((100% - var(--lp-note-h)) * ${lp.position})`,
              width: `${92 * lp.scale}%`,
            }}
          >
            <NoteCard note={note} look={look} onToggleLine={toggle} label={undefined} />
          </div>
        ) : (
          <p className="lp-empty">{note ? 'Empty note hidden (Settings → Show empty notes)' : 'No note selected'}</p>
        )}
        <div className="lp-corner is-left" aria-hidden="true">
          <Icon name="focus" size={20} />
        </div>
        <div className="lp-corner is-right" aria-hidden="true">
          <Icon name="phone" size={20} />
        </div>
        <div className="lp-home" aria-hidden="true" />
        <span className="lp-label">Preview</span>
      </div>
    </div>
  );

  return (
    <div className="screen preview">
      <ScreenHeader title="Lock-screen preview" onBack={() => goBack()} actions={<button type="button" className="btn is-small" onClick={() => setHelp(true)}>Help</button>} />
      <p className="lp-disclaimer">
        <Icon name="info" size={16} /> This is a simulation. Web apps can’t place widgets on the real lock screen — save a wallpaper image instead.
      </p>
      <div className="lp-stage">{phone}</div>

      {resolution.source === 'schedule' && note && (
        <p className="lp-sched">
          Shown by a schedule{resolution.until ? ` until ${formatDateTime(resolution.until)}` : ''}.
        </p>
      )}

      <div className="lp-actions">
        <button type="button" className="btn is-primary" disabled={!note || busy} onClick={() => void exportImage('wallpaper')}>
          Save lock-screen wallpaper
        </button>
        <button type="button" className="btn" disabled={!note || busy} onClick={() => void exportImage('note')}>
          Save note image
        </button>
        <button type="button" className="btn" onClick={() => setFull(true)}>
          Full screen
        </button>
      </div>

      <Group title="Note">
        <Row label="Shown note" htmlFor="lp-note">
          <select
            id="lp-note"
            value={manualActive ?? ''}
            onChange={(e) => {
              store.setActive(e.target.value || null);
              setAt(null);
            }}
          >
            {Object.values(notes)
              .filter((n) => !n.deletedAt)
              .sort((a, b) => b.updatedAt - a.updatedAt)
              .map((n) => (
                <option key={n.id} value={n.id}>
                  {displayTitle(n).slice(0, 40)}
                </option>
              ))}
          </select>
        </Row>
        <Row label="Preview at a time" detail={at === null ? 'Now' : 'Shows what a schedule would display'}>
          <input type="datetime-local" aria-label="Preview time" value={toLocalInput(at ?? now)} onChange={(e) => setAt(e.target.value ? new Date(e.target.value).getTime() : null)} />
          {at !== null && (
            <button type="button" className="btn is-small" onClick={() => setAt(null)}>
              Now
            </button>
          )}
        </Row>
        {note && (
          <Row label="Edit this note" icon="text" onClick={() => navigate({ name: 'note', id: note.id })} />
        )}
      </Group>

      <Group title="Look">
        <div className="lp-wallpapers" role="radiogroup" aria-label="Wallpaper">
          {WALLPAPERS.map((w) => (
            <button
              key={w.id}
              type="button"
              role="radio"
              aria-checked={!lp.customWallpaperAssetId && lp.wallpaper === w.id}
              aria-label={w.name}
              className="lp-wp"
              style={{ background: wallpaperCss(w) }}
              onClick={() => setLp({ wallpaper: w.id, customWallpaperAssetId: null })}
            />
          ))}
          <button type="button" className="lp-wp is-upload" aria-label="Use your own photo" aria-checked={!!lp.customWallpaperAssetId} role="radio" onClick={() => fileRef.current?.click()}>
            <Icon name="wallpaper" />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              try {
                const a = await putAsset(await importImage(f, f.name));
                setLp({ customWallpaperAssetId: a.id });
              } catch (err) {
                store.toast((err as Error).message, 'error');
              }
            }}
          />
        </div>
        <Row label="Position" htmlFor="lp-pos">
          <input id="lp-pos" type="range" min={0.15} max={0.92} step={0.01} value={lp.position} onChange={(e) => setLp({ position: Number(e.target.value) })} />
        </Row>
        <Row label="Size" htmlFor="lp-size">
          <input id="lp-size" type="range" min={0.6} max={1} step={0.01} value={lp.scale} onChange={(e) => setLp({ scale: Number(e.target.value) })} />
        </Row>
        {note && (
          <Row label="Text size" detail={note.style.autoSize ? 'Automatic' : `${note.style.fontScale.toFixed(1)}`} htmlFor="lp-fs">
            <input
              id="lp-fs"
              type="range"
              min={3.2}
              max={12}
              step={0.1}
              value={note.style.fontScale}
              onChange={(e) => store.updateNote(note.id, (n) => ({ ...n, style: { ...n.style, fontScale: Number(e.target.value), autoSize: false } }))}
            />
          </Row>
        )}
        {note && (
          <Row label="Compact note">
            <Switch label="Compact note" checked={note.style.compact} onChange={(v) => store.updateNote(note.id, (n) => ({ ...n, style: { ...n.style, compact: v } }))} />
          </Row>
        )}
        <Row label="Dim wallpaper for contrast">
          <Switch label="Dim wallpaper" checked={lp.contrast} onChange={(v) => setLp({ contrast: v })} />
        </Row>
        <Row label="Show clock">
          <Switch label="Show clock" checked={lp.showClock} onChange={(v) => setLp({ showClock: v })} />
        </Row>
        <Row label="Phone" htmlFor="lp-dev">
          <select id="lp-dev" value={lp.device} onChange={(e) => setLp({ device: e.target.value })}>
            {DEVICES.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Row>
      </Group>

      <Group>
        <Row label="How to install & use on your phone" icon="info" onClick={() => setHelp(true)} />
      </Group>

      {full && (
        <div className="lp-full" role="dialog" aria-modal="true" aria-label="Full-screen preview">
          {phone}
          <button type="button" className="btn lp-full-close" onClick={() => setFull(false)} autoFocus>
            Close preview
          </button>
        </div>
      )}
      <InstallHelp open={help} onClose={() => setHelp(false)} />
    </div>
  );
}
